import {
  LandingSection,
  LandingSectionContent,
  LandingSectionHeader,
  type LandingSectionProps,
  LandingSectionTitle,
} from "@/components/landing-page/landing-page-section";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { cn } from "@/lib/utils";
import { A } from "@solidjs/router";
import HeartPulseIcon from "lucide-solid/icons/heart-pulse";
import ListMusicIcon from "lucide-solid/icons/list-music";
import MoveRightIcon from "lucide-solid/icons/move-right";
import StethoscopeIcon from "lucide-solid/icons/stethoscope";
import TheaterIcon from "lucide-solid/icons/theater";
import UserIcon from "lucide-solid/icons/user";
import { lazy, splitProps } from "solid-js";

const Loom = lazy(() => import("@/components/loom-embed"));

export function LearnMoreSection(props: LandingSectionProps) {
  const [local, rest] = splitProps(props, ["class"]);

  return (
    <LandingSection class={cn("", local.class)} {...rest}>
      <LandingSectionHeader>
        <LandingSectionTitle>Overview</LandingSectionTitle>
      </LandingSectionHeader>
      <LandingSectionContent class="">
        <Card class="order-last flex grow flex-col border-border md:order-first">
          <CardHeader>
            <CardDescription class="font-semibold text-base text-muted-foreground/65 uppercase md:text-xl">
              Overview
            </CardDescription>
            <CardTitle class="text-lg max-md:max-w-2/3 sm:text-xl md:text-3xl">
              You'll leave with new skills to help your clients
            </CardTitle>
          </CardHeader>
          <Separator class="border-border" />
          <CardContent class="flex grow flex-col items-stretch justify-around bg-geometric pt-6">
            <div class="grid grid-cols-1 gap-8 md:grid-cols-2">
              <Loom
                videoId="723885ccb41c45ccbb521846bef0e33e"
                shareId="58fa8d1a-f990-4059-8baf-c43d920292ab"
                class=""
              />
              <ul class="grid grow auto-rows-fr grid-cols-1 gap-4 text-pretty text-center text-card-foreground text-lg lg:grid-cols-2 xl:grid-cols-1">
                <li class="flex grow flex-col items-center justify-center rounded-md border border-border bg-card px-2 py-4">
                  <ListMusicIcon class="size-18 text-muted-foreground" />
                  <p class="mt-4 inline-flex w-2/3 items-baseline font-medium lg:w-full xl:w-2/3">
                    Specialized team coaching skills to improve client team
                    harmony
                  </p>
                </li>
                <li class="flex grow flex-col items-center justify-center rounded-md border border-border bg-card px-2 py-4">
                  <StethoscopeIcon class="size-18 text-muted-foreground" />
                  <p class="mt-4 inline-flex w-2/3 items-baseline font-medium lg:w-full xl:w-2/3">
                    How to identify unproductive team toxins & their antidotes
                  </p>
                </li>
                <li class="flex grow flex-col items-center justify-center rounded-md border border-border bg-card px-2 py-4">
                  <HeartPulseIcon class="size-18 text-muted-foreground" />
                  <p class="mt-4 inline-flex w-2/3 items-baseline font-medium lg:w-full xl:w-2/3">
                    The four unique team types & how to diagnose and coach them
                    for success
                  </p>
                </li>
                <li class="flex grow flex-col items-center justify-center rounded-md border border-border bg-card px-2 py-4">
                  <TheaterIcon class="size-18 text-muted-foreground" />
                  <p class="mt-4 inline-flex w-2/3 items-baseline font-medium lg:w-full xl:w-2/3">
                    Solidify your new skills by participating in fun,
                    zero-stakes role-playing exercises
                  </p>
                </li>
                <li class="group col-span-full flex grow flex-col items-center justify-center rounded-md border border-border bg-card px-2 py-4">
                  <div class="relative h-18 w-18 text-muted-foreground">
                    <UserIcon class="-translate-x-1/3 absolute left-0 z-10 size-18" />
                    <UserIcon class="-translate-x-1/2 absolute left-1/2 z-20 size-18 translate-y-1 fill-card" />
                    <UserIcon class="absolute right-0 z-10 size-18 translate-x-1/3" />
                  </div>
                  <A
                    href="/instructors"
                    class="mt-4 inline-flex items-center self-center text-center font-medium underline-offset-2 group-hover:underline"
                  >
                    Meet the instructors
                    <MoveRightIcon class="ml-2 size-4 text-foreground/60 transition-all duration-50 group-hover:scale-[1.10] group-hover:text-foreground" />
                  </A>
                </li>
              </ul>
            </div>
          </CardContent>
        </Card>
      </LandingSectionContent>
    </LandingSection>
  );
}
